import * as React from "react";
import { useMemo } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useGetList, useTranslate } from "react-admin";
import { WithId } from "../../types";

interface RecordSelectorProps<T extends WithId> {
  resource: string;
  getOptionLabel: (option: T) => string | undefined;
  labelKey: string;
  placeholderKey: string;
  value: T | null;
  onChange: (option: T | null) => void;
  disabled?: boolean;
}

// `value`/`onChange` deal in the full option object (or null), not a bare
// id: deriving the selected option from a live useGetList result on every
// render is fragile (a transient empty/refetching list would blank out the
// display), so the parent keeps the chosen option in stable state instead.
const RecordSelector = <T extends WithId>({
  resource,
  getOptionLabel,
  labelKey,
  placeholderKey,
  value,
  onChange,
  disabled,
}: RecordSelectorProps<T>) => {
  const translate = useTranslate();

  const params = useMemo(
    () => ({
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" as const },
      filter: {},
    }),
    []
  );
  const { data, isLoading } = useGetList<T>(resource, params);

  const options = data || [];

  return (
    <Autocomplete
      sx={{ minWidth: 360, maxWidth: 480 }}
      size="small"
      options={options}
      loading={isLoading}
      disabled={disabled}
      value={value}
      onChange={(event, newValue) => onChange(newValue || null)}
      getOptionLabel={(option) => getOptionLabel(option) || option.id || ""}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translate(labelKey)}
          placeholder={translate(placeholderKey)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default RecordSelector;
